/**
 * 网络质量评估。
 *
 * 课堂上「听不清」「卡了」是最常见的抱怨，而老系统没有任何依据可查 ——
 * 只能靠双方互相猜是谁的网络有问题。这里从 WebRTC 的统计里取出
 * 丢包率与往返时延，给出一个能直说的结论。
 *
 * 判定用「上一秒到这一秒」的增量，而不是连接建立以来的累计值：
 * 累计值会把开头握手时的丢包一直算到下课，网络早就恢复了还显示很差。
 */

export type NetLevel = 'good' | 'fair' | 'poor' | 'unknown'

export interface NetQuality {
  level: NetLevel
  /** 丢包率，0~1。 */
  loss: number
  /** 往返时延，毫秒。取不到时为 -1。 */
  rtt: number
  text: string
}

/** 上一次采样，用于算增量。 */
interface Sample {
  lost: number
  total: number
}

export class NetMonitor {
  private last = new Map<string, Sample>()

  /** 采一次样。传入要观察的连接，通常是发布与订阅两条。 */
  async sample(pcs: Array<RTCPeerConnection | null>): Promise<NetQuality> {
    let lost = 0
    let total = 0
    let rtt = -1

    for (const [i, pc] of pcs.entries()) {
      if (!pc || pc.connectionState === 'closed') continue
      let stats: RTCStatsReport
      try {
        stats = await pc.getStats()
      } catch {
        continue
      }
      // 先收集本端发了多少包。
      //
      // remote-inbound-rtp 只有 packetsLost，**没有** packetsReceived ——
      // 拿它当分母的话上行方向的分母恒为 0，于是永远评不出结论。
      // 对端收了多少要用「我们发了多少」减去「对端说丢了多少」，
      // 而「我们发了多少」在 outbound-rtp 里。
      const sent = new Map<string, number>()
      stats.forEach((r) => {
        if (r.type === 'outbound-rtp') sent.set(String(r.ssrc ?? r.id), num(r.packetsSent))
      })

      stats.forEach((r) => {
        // 出站看 remote-inbound（对端告诉我们丢了多少），
        // 入站看 inbound —— 只看一边会漏掉另一个方向的问题。
        if (r.type === 'remote-inbound-rtp' || r.type === 'inbound-rtp') {
          const key = `${i}:${r.type}:${r.ssrc ?? r.id}`
          const packets =
            r.type === 'inbound-rtp'
              ? num(r.packetsReceived) + num(r.packetsLost)
              : sent.get(String(r.ssrc ?? r.id)) ?? 0
          const d = this.delta(key, num(r.packetsLost), packets)
          lost += d.lost
          total += d.total
        }
        if (r.type === 'candidate-pair' && r.nominated && r.currentRoundTripTime != null) {
          rtt = Math.round(Number(r.currentRoundTripTime) * 1000)
        }
        if (r.type === 'remote-inbound-rtp' && r.roundTripTime != null && rtt < 0) {
          rtt = Math.round(Number(r.roundTripTime) * 1000)
        }
      })
    }

    // 样本太少时不下结论：几个包的抖动能算出 50% 丢包率。
    if (total < 20) return { level: 'unknown', loss: 0, rtt, text: '正在评估网络…' }

    const loss = lost / total
    return { ...grade(loss, rtt), loss, rtt }
  }

  /** 算与上次的增量，并记下本次。 */
  private delta(key: string, lost: number, total: number): Sample {
    const prev = this.last.get(key)
    this.last.set(key, { lost, total })
    if (!prev || lost < prev.lost || total < prev.total) {
      // 计数器变小说明连接重建过，这一轮不计入。
      return { lost: 0, total: 0 }
    }
    return { lost: lost - prev.lost, total: total - prev.total }
  }

  /** 连接重建后清空基线。 */
  reset(): void {
    this.last.clear()
  }
}

/** 按丢包率与时延定级。阈值参照 WebRTC 通话的通行经验。 */
function grade(loss: number, rtt: number): { level: NetLevel; text: string } {
  if (loss >= 0.1 || rtt > 500) {
    return { level: 'poor', text: `网络很差（丢包 ${pct(loss)}${rttText(rtt)}）` }
  }
  if (loss >= 0.03 || rtt > 250) {
    return { level: 'fair', text: `网络一般（丢包 ${pct(loss)}${rttText(rtt)}）` }
  }
  return { level: 'good', text: `网络良好${rttText(rtt).replace('，', '，延迟')}` }
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`
}

function rttText(rtt: number): string {
  return rtt >= 0 ? `，${rtt}ms` : ''
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}
